import { datadogRum } from '@datadog/browser-rum';
import { log } from '@/logger/logger';
import { runGuarded } from '@/services/waf/runGuarded';

const logger = log.getLogger('token-refresh');

/**
 * Bounds a single ID-token refresh. `getIdToken()` returns a cached token synchronously and only hits
 * the network when the token is near expiry; Firebase caps that network refresh at ~30s (web) and the
 * Figma-embed provider self-bounds at its own `TOKEN_REQUEST_TIMEOUT_MS`, but the full-screen
 * boot/sign-in loader cannot wait that long, so this bounds it tighter. Kept above
 * `LOGOUT_DEADLINE_MS` (axiosConfig) so a logout's own deadline always fires first and owns that
 * path's timeout, leaving this error to only suppress the duplicate log there.
 */
export const TOKEN_REFRESH_DEADLINE_MS = 10_000;

export class TokenRefreshTimeoutError extends Error {
  constructor() {
    super('ID-token refresh timed out');
    this.name = 'TokenRefreshTimeoutError';
  }
}

// Concurrent requests each await their own refresh, so one stalled refresh could emit many warns;
// latch to one shipped warn per session.
let hasWarnedOnTokenRefreshTimeout = false;

export const TestOnlyResetTokenRefreshWarnLatch = (): void => {
  hasWarnedOnTokenRefreshTimeout = false;
};

const recordTimeout = (forceRefresh: boolean): void => {
  // Separate guard from the warn below so a throwing logger cannot also drop the vital.
  runGuarded(() => {
    datadogRum.addDurationVital('token_refresh', {
      startTime: Date.now() - TOKEN_REFRESH_DEADLINE_MS,
      duration: TOKEN_REFRESH_DEADLINE_MS,
      context: { outcome: 'timeout', forceRefresh },
    });
  });
  runGuarded(() => {
    if (!hasWarnedOnTokenRefreshTimeout) {
      // Latch only after the warn ships, so a throwing logger doesn't burn the one-per-session slot.
      logger.warn('ID-token refresh exceeded its deadline; failing the request', { forceRefresh });
      hasWarnedOnTokenRefreshTimeout = true;
    }
  });
};

const recordRefreshDuration = (durationMs: number): void => {
  runGuarded(() => {
    datadogRum.addDurationVital('token_refresh', {
      startTime: Date.now() - durationMs,
      duration: durationMs,
      context: { outcome: 'refreshed', forceRefresh: true },
    });
  });
};

/**
 * Fetches an ID token bounded by {@link TOKEN_REFRESH_DEADLINE_MS}. On timeout it rejects with a
 * {@link TokenRefreshTimeoutError} rather than leaving the caller hung on a stalled network refresh.
 * The timer rejects before any diagnostic runs, so a throwing emit can never wedge the deadline.
 */
export const getIdTokenWithDeadline = async <T extends string | null | undefined>(
  provider: { getIdToken: (forceRefresh?: boolean) => Promise<T> },
  forceRefresh = false,
): Promise<T> => {
  const startedAt = performance.now();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => reject(new TokenRefreshTimeoutError()), TOKEN_REFRESH_DEADLINE_MS);
  });
  try {
    const token = await Promise.race([provider.getIdToken(forceRefresh), deadline]);
    // Only a forced refresh is a single guaranteed network round-trip; recording that (rather than
    // every cached read, which fans out per request) is what sizes TOKEN_REFRESH_DEADLINE_MS.
    if (forceRefresh) {
      recordRefreshDuration(performance.now() - startedAt);
    }
    return token;
  } catch (error) {
    if (error instanceof TokenRefreshTimeoutError) {
      recordTimeout(forceRefresh);
    }
    throw error;
  } finally {
    if (timer !== undefined) {
      clearTimeout(timer);
    }
  }
};
